// Phase 12 — TopBar trial countdown pill + Sheet
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Clock, AlertTriangle, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBillingState, useBillingDaysLeft } from "@/store/useBillingStore";
import { useAssistantName } from "@/store/useTeamStore";
import { useAppStore } from "@/store/useAppStore";
import { billingApi } from "@/lib/api";
import { paidPlanBadgeLabel, planDisplayLabel } from "@/lib/planLabels";

const TRIAL_PILL_MAP = {
  "trial-active": { c: "bg-indigo-50 ring-indigo-200 text-indigo-700 hover:bg-indigo-100", Icon: Sparkles, labelKey: "days" },
  "trial-ending-soon": { c: "bg-amber-50 ring-amber-200 text-amber-800 hover:bg-amber-100", Icon: Clock, labelKey: "days" },
  "trial-expired": { c: "bg-rose-50 ring-rose-200 text-rose-700 hover:bg-rose-100", Icon: AlertTriangle, labelKey: "expired" },
};

const pillDisabledClass = "disabled:opacity-100 disabled:cursor-default hover:bg-inherit";

function trialDaysLabel(days) {
  return `Trial · ${days} day${days === 1 ? "" : "s"} left`;
}

function useServerPlan(authPlan) {
  const [serverPlan, setServerPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      billingApi.plan()
        .then((p) => { if (!cancelled) setServerPlan(p); })
        .catch(() => { if (!cancelled) setServerPlan(null); });
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; window.removeEventListener("focus", onFocus); };
  }, [authPlan]);

  return serverPlan;
}

function PaidPlanPill({ planId, serverPlan, onNavigate, disabled }) {
  const badge = paidPlanBadgeLabel(planId, serverPlan?.plan_label);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onNavigate("/settings/billing")}
      className={cn(
        "hidden md:inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 hover:bg-emerald-100",
        disabled && pillDisabledClass,
      )}
      data-testid="trial-pill-paid"
    >
      <CheckCircle2 size={10} /> {badge}
    </button>
  );
}

PaidPlanPill.propTypes = {
  planId: PropTypes.string,
  serverPlan: PropTypes.shape({ plan_label: PropTypes.string }),
  onNavigate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function TrialSheet({ open, onOpenChange, state, days, maya, onUpgrade }) {
  const upgradePlanLabel = planDisplayLabel("founder");
  const pct = state === "trial-expired" ? 100 : Math.max(0, Math.min(100, ((14 - days) / 14) * 100));
  let progressColor = "bg-indigo-500";
  if (state === "trial-expired") progressColor = "bg-rose-500";
  else if (state === "trial-ending-soon") progressColor = "bg-amber-500";

  let descriptionText;
  if (state === "trial-expired") {
    descriptionText = `${maya} stopped working when your trial ended. Bring her back.`;
  } else {
    descriptionText = `${days} day${days === 1 ? "" : "s"} left of your 14-day free trial.`;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] p-0" data-testid="trial-sheet">
        <SheetHeader className="px-5 py-4 border-b border-slate-100">
          <SheetTitle className="text-[14.5px] font-semibold inline-flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"><Sparkles size={13} /></span>
            {state === "trial-expired" ? "Trial ended" : "Your trial"}
          </SheetTitle>
          <SheetDescription className="text-[11.5px] text-slate-500">
            {descriptionText}
          </SheetDescription>
        </SheetHeader>
        <div className="p-5 space-y-5">
          <div>
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <span className="text-slate-500 uppercase tracking-wider font-semibold">Trial progress</span>
              <span className="font-mono tabular-nums text-slate-900">{state === "trial-expired" ? "Day 14 / 14" : `Day ${14 - days} / 14`}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className={cn("h-full rounded-full transition-all", progressColor)} style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">Recommended</div>
            <div className="mt-1 text-[15px] font-semibold text-slate-900">{upgradePlanLabel}</div>
            <div className="text-[18px] font-semibold tabular-nums text-indigo-700">$499<span className="text-[12px] text-slate-500 font-medium">/mo</span></div>
            <div className="mt-1 text-[11.5px] text-slate-600">Keep {maya} working on your WhatsApp, LinkedIn, Gmail — 24/7, in your voice.</div>
            <Button className="mt-3 w-full" onClick={onUpgrade} data-testid="trial-sheet-upgrade"><ArrowRight size={12} /> Upgrade now</Button>
          </div>

          <div className="text-[11px] text-slate-400 text-center">Cancel anytime · 14-day money-back guarantee</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function TrialPill({ disabled = false }) {
  const navigate = useNavigate();
  const state = useBillingState();
  const days = useBillingDaysLeft();
  const maya = useAssistantName();
  const { auth } = useAppStore();
  const [open, setOpen] = useState(false);
  const serverPlan = useServerPlan(auth?.server?.plan);

  const planId = serverPlan?.plan || auth?.server?.plan;
  const isPaid = serverPlan ? !serverPlan.is_trial : (planId && planId !== "trial") || state === "paid";

  if (isPaid) {
    return <PaidPlanPill planId={planId} serverPlan={serverPlan} onNavigate={navigate} disabled={disabled} />;
  }

  const cfg = TRIAL_PILL_MAP[state] || TRIAL_PILL_MAP["trial-active"];
  const label = cfg.labelKey === "expired" ? "Trial ended · Upgrade" : trialDaysLabel(days);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 text-[10.5px] font-semibold transition-colors",
          cfg.c,
          disabled && pillDisabledClass,
        )}
        data-testid="trial-pill"
      >
        <cfg.Icon size={10} /> {label}
      </button>
      <TrialSheet
        open={open}
        onOpenChange={setOpen}
        state={state}
        days={days}
        maya={maya}
        onUpgrade={() => { setOpen(false); navigate("/upgrade"); }}
      />
    </>
  );
}

TrialPill.propTypes = {
  disabled: PropTypes.bool,
};

TrialSheet.propTypes = {
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  state: PropTypes.string,
  days: PropTypes.number,
  maya: PropTypes.string,
  onUpgrade: PropTypes.func,
};
